import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
// import moment from 'moment';
// import buildExportQuery from './queryies/_export_all';
import { saveAs } from "file-saver";
import moment from "moment";
import {
  applyBasePrimeCostPrices,
  applyBasePrimeCostPricesFromChildren
} from "./utils/applyBasePrimeCostPrices";
import { shallowProductTypesTree } from "./utils/shallowProductTypesTree";

interface WorkPlanBoardState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  lastMeta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  documents: any[];
  groupedDocuments: any;
  productTypes: any[];
}

const state: WorkPlanBoardState = {
  loading: false,
  loaded: false,
  fatalError: false,

  lastMeta: {
    page: 0,
    pageSize: 0,
    totalCount: 0,
    totalPages: 0
  },

  documents: [],
  groupedDocuments: {},
  productTypes: []
};

export const ITEMS_LIST = "GET_PRODUCTS_LIST";
export const PRODUCT_TYPES_LIST = "GET_PRODUCT_TYPES_LIST";

const PARENT_DISPLAY_FIELDS = [
  "promo_name",
  "teaser_composition",
  "scope",
  "composition",
  "price_segment_name",
  "technology_name",
  "attachments",
  "label_attachments",
  "business_scopes",
  "protected_recipe"
];

function inheritParentDisplayFields(node: any, parent: any) {
  if (!parent) {
    return node;
  }
  const inherited: any = {};
  PARENT_DISPLAY_FIELDS.forEach(field => {
    if (parent[field] !== undefined && parent[field] !== null) {
      inherited[field] = parent[field];
    }
  });
  return {
    ...node,
    ...inherited,
    parent_product_name: parent.name,
    parent_product_trade_mark: parent.trade_mark_name
  };
}

function treeNodeKind(recordType: string) {
  return recordType === "parent_product" ? "parent" : "type";
}

const COLUMNS: any = {
  id: "products.id",
  product_name: "products.name",
  name: "products.name",
  code: "products.code",
  promo_name: "products_types.promo_name",
  archived_at: "products.archived_at",
  created_at: "products.created_at",
  updated_at: "products.updated_at",
  children: "grouped_product_containers.containers",
  type_id: "products.type_id",
  type_name: "products_types.name",
  teaser_composition: "products_types.teaser_composition",
  teaser: "products.teaser",
  trade_mark_name: "parent_trade_marks.name",
  attachments: "products_types.attachments",
  scope: "products_types.scope",
  composition: "products_types.composition",
  business_scopes: "grouped_business_scopes.scopes",
  price_segment_name: "parent_price_segments.name",
  technology_name: "parent_technologies.name"
};

function build_query(query: any, page_size?: number) {
  page_size = page_size ? page_size : 100000;
  const filter: any[] = [];
  const container_filter: any[] = [];
  if (query.name) {
    filter.push({ $ilike: ["products.name", { $: `%${query.name}%` }] });
  }
  return {
    columns: COLUMNS,
    from: "s:products",
    from_as: "products",
    joins: [
      {
        join: "s:products_types",
        type: "left",
        as: "products_types",
        on: { $eq: ["products_types.id", "products.type_id"] }
      },
      {
        join: "s:product_trade_marks",
        type: "left",
        as: "parent_trade_marks",
        on: {
          $eq: ["parent_trade_marks.id", "products_types.trade_mark_id"]
        }
      },
      {
        join: "s:product_technologies",
        type: "left",
        as: "parent_technologies",
        on: {
          $eq: ["parent_technologies.id", "products_types.technology_id"]
        }
      },
      {
        join: "s:product_price_segments",
        type: "left",
        as: "parent_price_segments",
        on: {
          $eq: ["parent_price_segments.id", "products_types.price_segment_id"]
        }
      },
      {
        join: {
          columns: {
            product_id: "product_business_scopes.product_id",
            scopes: {
              $array_agg: {
                $obj: {
                  id: "business_scopes.id",
                  name: "business_scopes.name"
                }
              }
            }
          },
          from: "s:product_business_scopes",
          from_as: "product_business_scopes",
          joins: [
            {
              join: "s:business_scopes",
              type: "left",
              as: "business_scopes",
              on: {
                $eq: [
                  "business_scopes.id",
                  "product_business_scopes.business_scope_id"
                ]
              }
            }
          ],
          groups: ["product_business_scopes.product_id"]
        },
        type: "left",
        as: "grouped_business_scopes",
        on: { $eq: ["grouped_business_scopes.product_id", "products.id"] }
      },
      {
        join: {
          columns: {
            product_id: "product_containers.product_id",
            containers: {
              $array_agg: {
                $obj: {
                  id: "product_containers.id",
                  name: "product_containers.name",
                  code_1c: "product_containers.sku_mixcart",
                  container_name: "product_containers.name",
                  product_id: "product_containers.product_id",
                  weight: "product_containers.weight",
                  archived_at: "product_containers.archived_at",
                  created_at: "product_containers.created_at",
                  updated_at: "product_containers.updated_at",
                  is_base_prime_cost: "product_containers.is_base_prime_cost",
                  cost: "prime_costs.cost",
                  trade_price: "prime_costs.trade_price",
                  retail_price: "prime_costs.retail_price"
                }
              }
            }
          },
          from: "s:product_containers",
          from_as: "product_containers",
          joins: [
            {
              join: "s:prime_costs",
              type: "left",
              as: "prime_costs",
              on: {
                $and: [
                  {
                    $eq: [
                      "prime_costs.product_container_id",
                      "product_containers.id"
                    ]
                  },
                  {
                    $eq: [
                      "prime_costs.month",
                      { $: moment().format("YYYY-MM-01") }
                    ]
                  }
                ]
              }
            }
          ],
          groups: ["product_containers.product_id"]
        },
        type: "left",
        as: "grouped_product_containers",
        on: { $eq: ["grouped_product_containers.product_id", "products.id"] }
      }
    ],
    page_size: page_size,
    ...{ page: parseInt(query.page) || 1 },
    ...{
      order: [
        query.sortDirection == "desc"
          ? { $desc: [COLUMNS[query.sortField || "name"]] }
          : { $asc: [COLUMNS[query.sortField || "name"]] }
      ]
    },
    ...(filter.length > 1
      ? {
          where: {
            $and: filter
          }
        }
      : {
          where: filter[0]
        })
  };
}

function build_export_query(query: any) {
  return {
    columns: COLUMNS,
    from: "s:products",
    from_as: "products",
    joins: [
      {
        join: {
          columns: {
            product_id: "product_containers.product_id",
            children: {
              $array_agg: {
                $obj: {
                  id: "product_containers.id",
                  name: "product_containers.name",
                  container_name: "product_containers.name",
                  archived_at: "product_containers.archived_at",
                  code_1c: "product_containers.sku_1c"
                }
              }
            }
          },
          from: "s:product_containers",
          from_as: "product_containers",
          groups: ["product_containers.product_id"]
        },
        type: "left",
        as: "grouped_product_containers",
        on: { $eq: ["grouped_product_containers.product_id", "products.id"] }
      }
    ]
  };
}

export const actions: ActionTree<WorkPlanBoardState, RootState> = {
  startLoading({ commit }) {
    commit("START_LOADING");
  },
  loadedSuccess({ commit }) {
    commit("LOADED_SUCCESS");
  },
  loadProductTypes({ commit }) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q(PRODUCT_TYPES_LIST)
          .raw("documents.list")
          .action({
            columns: {
              id: "products_types.id",
              name: "products_types.name",
              parent_id: "products_types.parent_id",
              record_type: "products_types.record_type",
              code: "products_types.code",
              promo_name: "products_types.promo_name",
              teaser_composition: "products_types.teaser_composition",
              scope: "products_types.scope",
              composition: "products_types.composition",
              attachments: "products_types.attachments",
              label_attachments: "products_types.label_attachments",
              technology_id: "products_types.technology_id",
              price_segment_id: "products_types.price_segment_id",
              trade_mark_id: "products_types.trade_mark_id",
              color: "products_types.color",
              protected_recipe: "products_types.protected_recipe",
              archived_at: "products_types.archived_at",
              technology_name: "product_technologies.name",
              price_segment_name: "product_price_segments.name",
              trade_mark_name: "parent_trade_marks.name",
              business_scopes: "grouped_business_scopes.scopes"
            },
            from: "s:products_types",
            from_as: "products_types",
            joins: [
              {
                join: "s:product_technologies",
                type: "left",
                as: "product_technologies",
                on: {
                  $eq: [
                    "product_technologies.id",
                    "products_types.technology_id"
                  ]
                }
              },
              {
                join: "s:product_price_segments",
                type: "left",
                as: "product_price_segments",
                on: {
                  $eq: [
                    "product_price_segments.id",
                    "products_types.price_segment_id"
                  ]
                }
              },
              {
                join: "s:product_trade_marks",
                type: "left",
                as: "parent_trade_marks",
                on: {
                  $eq: [
                    "parent_trade_marks.id",
                    "products_types.trade_mark_id"
                  ]
                }
              },
              {
                join: {
                  columns: {
                    product_type_id:
                      "product_type_business_scopes.product_type_id",
                    scopes: {
                      $array_agg: {
                        $obj: {
                          id: "business_scopes.id",
                          name: "business_scopes.name"
                        }
                      }
                    }
                  },
                  from: "s:product_type_business_scopes",
                  from_as: "product_type_business_scopes",
                  joins: [
                    {
                      join: "s:business_scopes",
                      type: "left",
                      as: "business_scopes",
                      on: {
                        $eq: [
                          "business_scopes.id",
                          "product_type_business_scopes.business_scope_id"
                        ]
                      }
                    }
                  ],
                  groups: ["product_type_business_scopes.product_type_id"]
                },
                type: "left",
                as: "grouped_business_scopes",
                on: {
                  $eq: [
                    "grouped_business_scopes.product_type_id",
                    "products_types.id"
                  ]
                }
              }
            ],
            page_size: 100000,
            order: [{ $asc: ["products_types.name"] }]
          })
      ])
        .then((result: ServerResult) => {
          const content = result.getOperation(PRODUCT_TYPES_LIST).getContent();
          const documents = content.documents || [];
          const byRecordType = documents.reduce((acc: any, row: any) => {
            const key = row.record_type || "type";
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});
          console.debug("[ProductCatalog.loadProductTypes]", {
            count: documents.length,
            byRecordType
          });
          commit("SET_PRODUCT_TYPES", documents);
          resolve(documents);
        })
        .catch((error: any) => {
          console.error("[ProductCatalog.loadProductTypes] error", error);
          reject(error);
        });
    });
  },

  loadData({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action(build_query(query))
      ]).then((result: ServerResult) => {
        const content = result.getOperation(ITEMS_LIST).getContent();
        commit("SET_ITEMS_LIST", content);
        commit("GROUP_BY_TYPE", content.documents);
        commit("LOADED_SUCCESS");

        resolve(null);
      });
    });
  },

  productToArchive({ rootState }, productId) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm.$q("PRODUCT_TO_ARCHIVE").update(productId, {
          archived_at: moment().format("YYYY-MM-DDTHH:mm:ss")
        })
      ])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("PRODUCT_TO_ARCHIVE");

          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  },

  productToUnarchive({ rootState }, productId) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm.$q("PRODUCT_TO_ARCHIVE").update(productId, {
          archived_at: null
        })
      ])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("PRODUCT_TO_ARCHIVE");

          if (operationResult.hasError()) {
            reject();
          } else {
            resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
          console.error("errror", error);
          reject(error);
        });
    });
  },

  getProductForCopy({ rootState }, productId) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("GET_PRODUCT_FOR_COPY")
          .raw("documents.get")
          .action({
            id: productId
          }),
        vm
          .$q("GET_PRODUCT_BUSINESS_SCOPES_FOR_COPY")
          .select()
          .from("product_business_scopes")
          .fileds({
            id: "product_business_scopes.id",
            product_id: "product_business_scopes.product_id",
            business_scope_id: "product_business_scopes.business_scope_id"
          })
          .where((_: any) => {
            return _.eq(
              "product_business_scopes.product_id",
              _.const(productId)
            );
          })
      ])
        .then((result: ServerResult) => {
          const productData = result
            .getOperation("GET_PRODUCT_FOR_COPY")
            .getContent();
          const businessScopes =
            result
              .getOperation("GET_PRODUCT_BUSINESS_SCOPES_FOR_COPY")
              .getContent().documents || [];

          resolve({
            document: productData.document,
            businessScopes
          });
        })
        .catch((error: any) => {
          console.error("Error getting product for copy", error);
          reject(error);
        });
    });
  },

  exportData({ state, getters }, params) {
    return new Promise((resolve, reject) => {
      try {
        const csvContent: any[] = [];
        const showColumns = params.showColumns || [];
        const allColumns = params.allColumns || [];

        // Формируем заголовки на основе выбранных колонок
        const header = allColumns
          .filter((col: any) => showColumns.includes(col.id))
          .map((col: any) => col.label);

        csvContent.push(header);

        const formatValue = (col: any, value: any, row?: any) => {
          if (col.format && typeof col.format === "function") {
            return col.format(value, row);
          }
          if (value === null || value === undefined) {
            return "";
          }
          if (Array.isArray(value)) {
            if (value.length === 0) return "";
            // Для массивов объектов (например, business_scopes)
            if (typeof value[0] === "object" && value[0].name) {
              return value.map((item: any) => item.name).join(", ");
            }
            return value.join(", ");
          }
          return String(value);
        };

        const pushNodeRow = (node: any) => {
          const row: any[] = [];
          allColumns
            .filter((col: any) => showColumns.includes(col.id))
            .forEach((col: any) => {
              const value = node[col.id];
              row.push(formatValue(col, value, node));
            });
          csvContent.push(row);
        };

        // Shallow export: type → parent only (matches main catalog page)
        const processTree = (nodes: any[]) => {
          nodes.forEach((node: any) => {
            if (node.type === "type" || node.type === "parent") {
              pushNodeRow(node);
              if (node.children && node.children.length > 0) {
                processTree(node.children);
              }
            }
          });
        };

        const tree = shallowProductTypesTree(getters.productTypesTree);
        console.debug("[ProductCatalog.exportData] shallow export", {
          roots: tree.length
        });
        processTree(tree);

        resolve(csvContent);
      } catch (error) {
        console.error("Error exporting data", error);
        reject(error);
      }
    });
  },

  exportDataAsXLSX({ rootState }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      delete query.page;
      const action = build_query(query, 100000);
      const TEMPLATE =
        "<!DOCTYPE html>\n" +
        '<table font=Verdana font-size=11 orientation="portrait" area="A:C" scale="100">\n' +
        "  <tr height=30>\n" +
        "    <td wrap border bold bgcolor=FF72BF44 width=30>Наименование продукта</td>\n" +
        "    <td wrap border bold bgcolor=FF72BF44 width=15>Тип тары</td>\n" +
        "    <td wrap border bold bgcolor=FF72BF44 width=15>Артикул 1С</td>\n" +
        "  </tr>\n" +
        "\n" +
        "  {{#products.documents}}\n" +
        "    {{#containers}}\n" +
        '    <tr height="auto">\n' +
        "        <td wrap border>{{product_name}}</td>\n" +
        "        <td wrap border>{{container_name}}</td>\n" +
        "        <td wrap border align=center>{{code_1c}}</td>\n" +
        "        </tr>\n" +
        "    {{/containers}}\n" +
        "  {{/products.documents}}\n" +
        "</table>\n";

      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action({
            template: TEMPLATE,
            queries: {
              products: action
            },
            format: "xlsx"
          })
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve(null);
      });
    });
  }
};

export const mutations: MutationTree<WorkPlanBoardState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_ITEMS_LIST(state, data) {
    state.documents = data.documents;

    state.lastMeta = {
      page: data.meta.page,
      pageSize: data.meta.page_size,
      totalCount: data.meta.total_count,
      totalPages: data.meta.total_pages
    };
  },

  GROUP_BY_TYPE(state, documents) {
    const grouped: any = {};

    documents.forEach((doc: any) => {
      const typeId = doc.type_id || "no_type";
      const typeName = doc.type_name || "Без типа";
      const containers = (doc.children || []).map((item: any) => ({
        ...item,
        type: "container",
        product_name: doc.name
      }));

      if (!grouped[typeId]) {
        grouped[typeId] = {
          type_id: typeId,
          type_name: typeName,
          products: []
        };
      }

      grouped[typeId].products.push({
        ...doc,
        type: "product",
        children: containers
      });
    });

    state.groupedDocuments = grouped;
  },

  SET_PRODUCT_TYPES(state, types) {
    state.productTypes = types;
  }
};

const getters = {
  productTypes: (state: WorkPlanBoardState) => state.productTypes,
  productTypesMap: (state: WorkPlanBoardState) => {
    return state.productTypes.reduce((map: any, type: any) => {
      map[type.id] = type;
      return map;
    }, {});
  },
  productTypesTree: (state: WorkPlanBoardState) => {
    const types = state.productTypes;
    const typeMap: any = {};
    const roots: any[] = [];

    types.forEach((type: any) => {
      typeMap[type.id] = {
        ...type,
        record_type: type.record_type || "type",
        type: treeNodeKind(type.record_type || "type"),
        children: []
      };
    });

    types.forEach((type: any) => {
      const node = typeMap[type.id];
      const recordType = type.record_type || "type";
      if (
        recordType === "parent_product" &&
        type.parent_id &&
        typeMap[type.parent_id]
      ) {
        typeMap[type.parent_id].children.push(node);
      } else if (recordType === "type") {
        roots.push(node);
      }
    });

    Object.values(state.groupedDocuments).forEach((group: any) => {
      const parentNode = typeMap[group.type_id];
      if (!parentNode || parentNode.record_type !== "parent_product") {
        return;
      }
      group.products.forEach((product: any) => {
        let enriched = inheritParentDisplayFields(product, parentNode);
        if (enriched.children) {
          enriched.children = enriched.children.map((container: any) =>
            inheritParentDisplayFields(container, enriched)
          );
        }
        enriched = applyBasePrimeCostPrices(enriched);
        parentNode.children.push(enriched);
      });
    });

    Object.values(typeMap).forEach((node: any) => {
      if (node.record_type !== "parent_product") {
        return;
      }
      const priced = applyBasePrimeCostPricesFromChildren(node);
      node.cost = priced.cost;
      node.trade_price = priced.trade_price;
      node.retail_price = priced.retail_price;
    });

    console.debug("[ProductCatalog.productTypesTree]", {
      roots: roots.length,
      parents: types.filter((t: any) => t.record_type === "parent_product")
        .length,
      products: Object.values(state.groupedDocuments).reduce(
        (count: number, group: any) => count + group.products.length,
        0
      )
    });

    return roots;
  }
};

export const STORE_KEY = "$_product_catalog";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
